import { IDynamicQueryCollection } from '../@types/DynamicInsightQueryCollection';
import { ICollectionQuestion } from '../@types/CollectionQuestion';
import apiClient from './apiClient';

const deserializeQuestions = (questions: any): ICollectionQuestion[] => {
    if (!questions) return [];

    if (Array.isArray(questions)) {
        return questions.map((question, index) => ({
            attribute: question.alias || question.attribute || `question_${index}`,
            question: question.Question || question.question || '',
            questionType: question.QuestionType || question.question_type || '',
            group: question.group || '',
            validAnswers: question.ValidAnswers || question.valid_answers || [],
        }));
    }

    return Object.entries(questions).map(([attribute, question]: [string, any]) => ({
        attribute,
        question: question.Question || question.question || '',
        questionType: question.QuestionType || question.question_type || '',
        group: question.group || '',
        validAnswers: question.ValidAnswers || question.valid_answers || [],
    }));
};

const serializeQuestions = (questions: ICollectionQuestion[]): Record<string, any> =>
    Object.fromEntries(
        questions
            .filter((question) => question.attribute && question.question)
            .map((question) => [
                question.attribute,
                {
                    group: question.group || '',
                    Question: question.question,
                    QuestionType: question.questionType,
                    ValidAnswers: (question.validAnswers || []).map((answer) => answer?.trim()),
                },
            ])
    );

const deserializeCollection = (collection: any): IDynamicQueryCollection => ({
    id: collection.id,
    name: collection.name,
    questions: deserializeQuestions(collection.questions),
});

export const fetchQuestionCollections = async (params?) => {
    return apiClient
        .get(`/qcollections`, { params })
        .then((res) => ({
            ...res.data,
            items: res.data.items?.map(deserializeCollection) || [],
        }))
        .catch((err) => Promise.reject({ ...err }));
};

export const saveQuestionCollection = async (data: IDynamicQueryCollection) => {
    return apiClient
        .post(`/qcollections`, {
            name: data.name,
            questions: serializeQuestions(data.questions),
        })
        .then((res) => res.data)
        .catch((err) => Promise.reject({ ...err }));
};

export const getRecommendedType = async (question: string, provided_type: string) => {
    return apiClient
        .post(`/qcollections/recommended_qtypes`, [
            {
                question,
                provided_type,
            },
        ])
        .then((res) => res.data)
        .catch((err) => Promise.reject({ ...err }));
};

export const updateQuestionCollection = async (data: IDynamicQueryCollection) => {
    return apiClient
        .put(`/qcollections/${data.id}`, {
            name: data.name,
            questions: serializeQuestions(data.questions),
        })
        .then((res) => res.data)
        .catch((err) => Promise.reject({ ...err }));
};

export const deleteQuestionCollection = async (collectionId: string) => {
    return apiClient.delete(`/qcollections/${collectionId}`).then((res) => res.data);
};

export const fetchQuestionTypes = async () => {
    return apiClient.get('qcollections/qtypes').then((res) => res.data);
};

export const runDynamicQADoc = async (docIds: string[], collectionId: string) => {
    return apiClient
        .get('documents/qa_dynamic', {
            params: {
                doc_ids: docIds,
                collection_id: collectionId,
            },
            paramsSerializer: {
                indexes: null,
            },
        })
        .then((res) => res.data);
};
